import {
  cloneElement,
  forwardRef,
  isValidElement,
  MouseEvent,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Tooltip as MantineTooltip,
  TooltipProps as MantineTooltipProps,
} from '@mantine/core'
import styled, { useTheme } from 'styled-components'
import { TooltipProps } from './tooltip.types'
import { Theme } from '../../theme'
import { mergeRefs } from '../../util'
import { useClickOutside } from '../../util/useClickOutside'

const DEFAULT_LINE_CLAMP = 8

// Styled via Mantine's `styles.tooltip` (not a styled-components className) so the
// surface lands on the tooltip element only and never on the trigger.
const getTooltipStyles = (
  theme: Theme,
  lightBg: boolean | undefined,
  shadowSize: 'small' | 'medium',
  width?: number
): MantineTooltipProps['styles'] => ({
  tooltip: {
    boxSizing: 'border-box',
    width: width ? `${width}px` : 'max-content',
    // Figma max width is 320px; never wider than the viewport minus gutters.
    maxWidth: `min(320px, calc(100vw - 2 * ${theme.spacing.m}))`,
    padding: `${theme.spacing.xs} ${theme.spacing.s}`,
    // Figma cornerRadiusTooltip is 2px; no radius token this small.
    borderRadius: 2,
    // Figma background/secondary-invert is #302c38 (neutral800), but
    // theme.semantic.backgroundSecondaryInvert resolves to neutral600.
    backgroundColor: lightBg
      ? theme.semantic.backgroundSecondary
      : theme.scale.neutral800,
    color: lightBg
      ? theme.semantic.contentPrimary
      : theme.semantic.contentInvert,
    boxShadow: theme.boxShadows[shadowSize],
    fontFamily: 'inherit',
    fontSize: theme.typographyTokens.bodySm.fontSize,
    fontWeight: 600,
    // No token for semibold 12/16 with 0.2px tracking.
    lineHeight: '16px',
    letterSpacing: '0.2px',
    whiteSpace: 'normal',
    wordWrap: 'break-word',
  },
})

const ClampedText = styled.div<{ $lineClamp: number }>`
  ${({ $lineClamp }) =>
    $lineClamp
      ? `
    display: -webkit-box;
    -webkit-line-clamp: ${$lineClamp};
    -webkit-box-orient: vertical;
  `
      : 'display: block;'}
  overflow: hidden;
  /* Fixes letters with descenders being clipped */
  padding: 1px;
`

export const Tooltip = forwardRef<HTMLElement, TooltipProps>(
  (
    {
      children,
      content,
      lightBg,
      lineClamp,
      shadowSize = 'medium',
      width,
      placement = 'top',
      trigger = 'hover',
      autoClose = false,
      offsetValue = 8,
    },
    ref
  ) => {
    const theme = useTheme() as Theme
    const isClick = trigger === 'click'
    const [opened, setOpened] = useState(false)
    const anchorRef = useRef<HTMLElement | null>(null)
    const timeoutRef = useRef<ReturnType<typeof setTimeout>>()

    const close = useCallback(() => setOpened(false), [])
    useClickOutside(anchorRef as React.RefObject<HTMLElement>, () => {
      if (isClick && opened) close()
    })

    // Click mode is driven by `opened`; auto-close timer restarts on each open.
    useEffect(() => {
      if (!isClick || !opened) return undefined
      if (autoClose !== false) {
        timeoutRef.current = setTimeout(close, autoClose)
      }
      const onKeyDown = (e: KeyboardEvent) => e.key === 'Escape' && close()
      document.addEventListener('keydown', onKeyDown)
      return () => {
        clearTimeout(timeoutRef.current)
        document.removeEventListener('keydown', onKeyDown)
      }
    }, [isClick, opened, autoClose, close])

    const mergedRef = useMemo(
      () => mergeRefs<HTMLElement>([anchorRef, ref]),
      [ref]
    )

    // eslint-disable-next-line react/jsx-no-useless-fragment
    if (!content) return <>{children}</>

    // Mantine needs a single ref-accepting child.
    const anchor = isValidElement(children) ? (
      (children as ReactElement)
    ) : (
      <span>{children}</span>
    )

    const trigged = isClick
      ? cloneElement(anchor, {
          onClick: (e: MouseEvent) => {
            anchor.props.onClick?.(e)
            setOpened((o) => !o)
          },
        })
      : anchor

    return (
      <MantineTooltip
        ref={mergedRef}
        label={
          <ClampedText $lineClamp={lineClamp ?? DEFAULT_LINE_CLAMP}>
            {content}
          </ClampedText>
        }
        position={placement}
        offset={offsetValue}
        withinPortal
        transitionProps={{ duration: 0 }}
        multiline
        role="tooltip"
        zIndex={Number(theme.zIndex.modal) + 1}
        // Figma: content must be visible on focus. Mantine's default is hover only.
        events={{ hover: true, focus: true, touch: false }}
        {...(isClick ? { opened } : {})}
        styles={getTooltipStyles(theme, lightBg, shadowSize, width)}
      >
        {trigged}
      </MantineTooltip>
    )
  }
)
